/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';
import {
  Program,
  AppError,
  ProgramInput,
  AudioOptionInput,
  AudioOption,
} from 'models';
import { MediaType, OrderUpdate } from 'models/forms';
import { buildFilePath } from 'helpers/firebase';
import Programs from 'services/firebase/database/programs';
import useStorage from './useStorage';

interface ProgarmsHookResponse {
  programs: Program[];
  loading: boolean;
  error: null | AppError;
  getAll: (isSubscriptionRequired: boolean) => Promise<void>;
  add: (program: ProgramInput) => Promise<boolean>;
  remove: (program: Program) => Promise<boolean>;
  update: (program: Program, newProgram: ProgramInput) => Promise<boolean>;
  getExistingOrders: (isSubscriptionRequired: boolean) => Promise<number[]>;
}

const usePrograms = (): ProgarmsHookResponse => {
  const { remove: removeFile, upload } = useStorage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null as null | AppError);
  const [programs, setPrograms] = useState([] as Program[]);

  // Local hook functions.
  const uploadAudioOption = async (
    audio: AudioOptionInput,
  ): Promise<AudioOption | null> => {
    let audioTrack = audio.audioTrack.file;
    let padsTrack = audio.padsTrack.file;

    if (audio.audioTrack.file && typeof audio.audioTrack.file !== 'string') {
      audioTrack = (await upload(
        audio.audioTrack.file as File,
        MediaType.AUDIO,
        audio.audioTrack.name,
      )) as string;
    }
    if (audio.padsTrack.file && typeof audio.padsTrack.file !== 'string') {
      padsTrack = (await upload(
        audio.padsTrack.file as File,
        MediaType.AUDIO,
        audio.padsTrack.name,
      )) as string;
    }
    if (audioTrack) {
      return {
        audioTrack: audioTrack as string,
        padsTrack: padsTrack as string,
        duration: audio.duration || 0,
      };
    }
    return null;
  };

  const updateOrder = async (
    newOrder: number,
    isSubscriptionRequired: boolean,
    isNew: boolean,
    programId?: string,
  ): Promise<boolean> => {
    let result = false;
    const affectedPrograms = await Programs.getAllBySubscriptionType<Program>(
      isSubscriptionRequired,
    );
    let order = 0;
    const updates = [];
    if (affectedPrograms.type === 'success') {
      const programsList = isNew
        ? affectedPrograms.data
        : affectedPrograms.data.filter((program) => program.id !== programId);

      for (const program of programsList) {
        const { id, ...programToUpdate } = program;
        if (id) {
          if (order === newOrder) {
            order += 1;
          }
          updates.push(
            Programs.update<Program>(id, { ...programToUpdate, order }),
          );
        }
        order += 1;
      }
      const updated = await Promise.all(updates);
      result = !updated
        .map((update) => update?.type === 'success')
        .includes(false);
    }
    return result;
  };

  const updateOrderOnDelete = async (
    isSubscriptionRequired: boolean,
  ): Promise<boolean> => {
    let result = false;
    const affectedPrograms = await Programs.getAllBySubscriptionType<Program>(
      isSubscriptionRequired,
    );
    let order = 0;
    const updates = [];
    if (affectedPrograms.type === 'success') {
      for (const program of affectedPrograms.data) {
        const { id, ...programToUpdate } = program;
        if (id) {
          updates.push(
            Programs.update<Program>(id, { ...programToUpdate, order }),
          );
        }
        order += 1;
      }
      const updated = await Promise.all(updates);
      result = !updated
        .map((update) => update?.type === 'success')
        .includes(false);
    }
    return result;
  };

  // Exported hook functions.
  const getAllPrograms = async (
    isSubscriptionRequired: boolean,
  ): Promise<void> => {
    const result = await Programs.getAllBySubscriptionType<Program>(
      isSubscriptionRequired,
    );
    switch (result.type) {
      case 'success':
        setPrograms(result.data);
        break;
      case 'error':
        setError(result.error);
        break;
      default:
    }
    setLoading(false);
  };

  const getOrders = async (
    isSubscriptionRequired: boolean,
  ): Promise<number[]> => {
    const result = await Programs.getOrders<number>(isSubscriptionRequired);
    return result.type === 'success' ? result.data : [];
  };

  const addProgram = async (program: ProgramInput): Promise<boolean> => {
    const { video, options, ...rest } = program;
    // Upload Audios.
    const audioUploads = options
      .map((option) => uploadAudioOption(option))
      .filter((option) => option);
    const uploadedAudios = await Promise.all(audioUploads);

    // Upload Video.
    const videoURL = await upload(
      program.video.file as File,
      MediaType.VIDEO,
      program.video.name,
    );

    // Upload Image.
    const imageURL = await upload(program.imageURL as Blob, MediaType.IMAGE);
    let result = false;

    if (uploadedAudios.length === options.length && videoURL && imageURL) {
      const newProgram: Program = {
        ...rest,
        options: uploadedAudios as AudioOption[],
        imageURL,
        videoURL,
      };

      // Update existing programs order if needed.
      const orderUpdated = await updateOrder(
        newProgram.order,
        newProgram.isSubscriptionRequired,
        true,
      );

      // Add Program.
      if (orderUpdated) {
        const createdProgram = await Programs.add<Program>(newProgram);
        result = createdProgram.type === 'success';
      }
    }
    return result;
  };

  const updateProgram = async (
    program: Program,
    newProgram: ProgramInput,
  ): Promise<boolean> => {
    const { video, options, ...rest } = newProgram;
    let { imageURL } = newProgram;
    let { file: videoURL } = newProgram.video;
    let orderUpdated = program.order === newProgram.order;

    // Upload Audios.
    const audioUploads = options
      .map((option) => {
        return uploadAudioOption(option);
      })
      .filter((option) => option);
    const uploadedAudios = await Promise.all(audioUploads);

    // Upload Video.
    if (newProgram.video.file && typeof newProgram.video.file !== 'string') {
      const newVideoURL = await upload(
        newProgram.video.file as File,
        MediaType.VIDEO,
        newProgram.video.name,
      );
      if (!newVideoURL) return false;
      videoURL = newVideoURL;
    }

    // Upload Image.
    if (newProgram.imageURL && typeof newProgram.imageURL !== 'string') {
      const newImageURL = await upload(newProgram.imageURL, MediaType.IMAGE);
      if (!newImageURL) return false;
      imageURL = newImageURL;
    }

    let result = false;

    if (uploadedAudios.length === options.length && videoURL && imageURL) {
      const programToUpdate: Program = {
        ...rest,
        options: uploadedAudios as AudioOption[],
        imageURL: imageURL as string,
        videoURL: videoURL as string,
      };

      // Update existing programs order if needed.
      if (program.order !== newProgram.order) {
        orderUpdated = await updateOrder(
          newProgram.order,
          newProgram.isSubscriptionRequired,
          false,
          program.id,
        );
      }

      // Add Program.
      if (orderUpdated && program.id) {
        const createdProgram = await Programs.update<Program>(
          program.id,
          programToUpdate,
        );
        result = createdProgram.type === 'success';
      }
    }
    return result;
  };

  const removeProgram = async (program: Program): Promise<boolean> => {
    // Remove image.
    const imagePath = buildFilePath(program.imageURL, MediaType.IMAGE);
    await removeFile(imagePath);

    // Remove video.
    const videoPath = buildFilePath(program.videoURL, MediaType.VIDEO);
    await removeFile(videoPath);

    // Remove audios.
    const audioUploads = program.options
      .map((option) => {
        const audioPath = buildFilePath(option.audioTrack, MediaType.AUDIO);
        const padsPath = buildFilePath(option.padsTrack, MediaType.AUDIO);
        return removeFile(audioPath) && removeFile(padsPath);
      })
      .filter((option) => option);
    await Promise.all(audioUploads);

    const result = await Programs.remove(program.id!);
    if (result.type === 'success') {
      // Update order.
      await updateOrderOnDelete(program.isSubscriptionRequired);
      return true;
    }
    return false;
  };

  return {
    programs,
    loading,
    error,
    getAll: getAllPrograms,
    getExistingOrders: getOrders,
    add: addProgram,
    update: updateProgram,
    remove: removeProgram,
  };
};

export default usePrograms;
